import { MutationStatus, useAppStorage, useMutation, useObservable } from '@aesop-fables/scrinium';
import { FormControl, Typography, Grid, Box } from '@mui/material';
import { useState, useEffect, useContext, useMemo } from 'react';
import {
  OrganizationMetadataCompartments,
  organizationMetadataStorageKey,
} from '../../data/organizationMetadata';
import { OrganizationMetadata } from '../../models/OrganizationMetadata';
import { useService } from '@aesop-fables/containr-react';
import { useNavigate, useLocation } from 'react-router';
import { ToastbarContext } from '../../App';
import { Column, Spacer, StepsHeader, ButtonFooter } from '../../components';
import { OrganizationCompartments, organizationStorageKey } from '../../data/organization';
import { CreateCustomBlurb, UpdateCustomBlurb } from '../../data/organizationMetadata/mutations';
import { UpdateSignUpState } from '../../data/signUp/mutations';
import { useLoading } from '../../hooks/useLoading';
import { OrganizationData } from '../../models/OrganizationData';
import { Toast } from '../../models/Toast';
import { OffboardingWizard, offboardingWizardKey } from '../../services/offboarding';
import { SignUpCompartments, signUpStorageKey } from '../../data/signUp';
import { OrgSignUpStateData } from '../../models/SignUpData';
import CustomBlurbTextBox from './CustomBlurbTextBox';

const CustomBlurb = () => {
  const appStorage = useAppStorage();
  const navigate = useNavigate();
  const location = useLocation();
  const { setToast } = useContext(ToastbarContext);
  const { setLoading } = useLoading();

  const updateSignUpState = useMutation(new UpdateSignUpState());
  const createCustomBlurb = useMutation(new CreateCustomBlurb());
  const updateCustomBlurb = useMutation(new UpdateCustomBlurb());

  const offboardingWizard = useService<OffboardingWizard>(offboardingWizardKey);
  const signUpDataCache = appStorage.retrieve<SignUpCompartments>(signUpStorageKey);
  const organizationDataCache =
    appStorage.retrieve<OrganizationCompartments>(organizationStorageKey);
  const organizationMetadataCache = appStorage.retrieve<OrganizationMetadataCompartments>(
    organizationMetadataStorageKey,
  );

  const orgSignUpState = useObservable(
    signUpDataCache.observe$<OrgSignUpStateData>('orgSignUpState'),
  );
  const { offboardingComplete } = orgSignUpState ?? { offboardingComplete: false };
  const organizationData = useObservable(
    organizationDataCache.observe$<OrganizationData>('organization'),
  );
  const organizationMetadata = useObservable(
    organizationMetadataCache.observe$<OrganizationMetadata>('metadata'),
  );
  const saveCustomBlurb = useMemo(() => {
    return !organizationMetadata ? createCustomBlurb : updateCustomBlurb;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationMetadata]);

  const [isLimitExceeded, setIsLimitExceeded] = useState(false);
  const [customBlurb, setCustomBlurb] = useState('');

  const current = location.pathname;
  const { activeStep, totalSteps } = offboardingWizard.getStepInfo(current);

  useEffect(() => {
    if (saveCustomBlurb.error) {
      setToast(
        new Toast({
          severity: 'error',
          open: true,
          message: 'Error saving custom blurb. Please try again.',
        }),
      );
      return;
    } else if (saveCustomBlurb.status === MutationStatus.Complete) {
      setToast(
        new Toast({
          severity: 'success',
          open: true,
          message: 'Custom blurb saved!',
          autoHideDuration: 3000,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveCustomBlurb.status]);

  const navBack = () => {
    navigate(-1);
  };

  const onFinish = async () => {
    setLoading(true);
    const next = offboardingWizard.nextStep(location);

    if (activeStep === totalSteps) {
      if (!offboardingComplete) {
        offboardingWizard.offboardingCompleted();
        await organizationDataCache.reload('organization');
      }
    }
    await saveCustomBlurb.action({
      organizationId: organizationData?.id ?? 0,
      signupPageText: customBlurb,
    });

    await updateSignUpState.action({
      lastCompletedStep: current,
      offboardingComplete: activeStep === totalSteps,
    });

    navigate(next.route);
    setLoading(false);
  };

  return (
    <Column
      style={{
        padding: '0px 100px 150px 100px',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Spacer height='xs' />
      <StepsHeader activeStep={activeStep} totalSteps={totalSteps} />
      <Spacer height='xs' />

      <Grid style={{ flexGrow: 1 }}>
        <Typography variant='p50Light' color='secondary'>
          Customize messaging
        </Typography>
        <Spacer height='xs' />
        <Typography variant='p20SemiBold' color='secondary'>
          Want to say something to your members on your custom sign up page?
        </Typography>
        <Spacer height='xxs' />
        <Box
          sx={{
            width: '100%',
            maxWidth: { md: '100%', lg: '75%', xl: '65%' },
          }}>
          <Typography
            variant='p16'
            fontStyle='italic'
            color='info.main'
            sx={{
              flexShrink: 1,
              flexGrow: 0,
              maxWidth: {
                md: '100%',
                lg: '75%',
                xl: '65%',
              },
            }}>
            Example: Welcome to your gateway to financial wellness with 3Nickels, the innovative app
            for financial health. This portal unlocks access to essential tools and resources for
            your financial journey. From budgeting, investing, and saving to personalized financial
            advice, 3Nickels simplifies personal finance, empowering you towards financial freedom.
            Start now and embrace your path to financial wellness.
          </Typography>
        </Box>
        <Spacer height='xxs' />
        <FormControl style={{ width: '50%' }}>
          <CustomBlurbTextBox
            onLimitExceeded={overLimit => setIsLimitExceeded(overLimit)}
            onChange={setCustomBlurb}
          />
        </FormControl>
      </Grid>

      <ButtonFooter
        primaryButton={{
          label: activeStep === totalSteps ? `Done` : `Next`,
          onClick: onFinish,
          className: 'next',
          disabled: isLimitExceeded,
        }}
        secondaryButton={{
          label: 'Back',
          onClick: navBack,
          showCondition: true,
        }}
      />
    </Column>
  );
};

export default CustomBlurb;
